// src/hooks/useSingleUserPresence.ts
import { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { User } from "../../../models/user";
import { useWebSocket } from "../useWebSocket";

// même store interne que pour useEnriched…
const listeners = new Map<
  string,
  { handlers: Set<(data: any) => void>; stompSub: any }
>();
const presenceCache: Record<string, { connected: boolean; lastSeen: string }> =
  {};

// identical to before
function subscribeToPresence(
  userId: string,
  callback: (data: any) => void,
  client: ReturnType<typeof useWebSocket>["subscribe"]
) {
  if (!listeners.has(userId)) {
    const handlers = new Set<(data: any) => void>();
    const stompSub = client(`/topic/presence/${userId}`, (msg) => {
      const data = JSON.parse(msg.body);
      presenceCache[userId] = data;
      handlers.forEach((cb) => cb(data));
    });
    listeners.set(userId, { handlers, stompSub });
  }
  listeners.get(userId)!.handlers.add(callback);
  if (presenceCache[userId]) callback(presenceCache[userId]);
}

function unsubscribeFromPresence(
  userId: string,
  callback: (data: any) => void
) {
  const entry = listeners.get(userId);
  if (!entry) return;
  entry.handlers.delete(callback);
  if (entry.handlers.size === 0) {
    entry.stompSub.unsubscribe();
    listeners.delete(userId);
    // on peut garder le cache ou le clear, mais ce n’est pas nécessaire
  }
}

/**
 * Hook pour gérer la présence d’UN SEUL user.
 * - Renvoie [user, setUser], où setUser déclenche la subscription STOMP.
 */
export function useSingleUserPresence() {
  const [user, _setUser] = useState<User | null>(null);
  const callbackRef = useRef<(d: any) => void>(() => {});
  const client = useWebSocket().subscribe;

  const setUser = (u: User) => {
    // désabonner l’ancien si besoin
    if (user && callbackRef.current) {
      unsubscribeFromPresence(user.id, callbackRef.current);
    }
    // seed initial state
    _setUser(u);

    // subscribe au nouveau user.id
    const handler = (presence: {
      connected: boolean;
      lastSeen: Dayjs | null;
    }) => {
      _setUser((cur) =>
        cur && cur.id === u.id
          ? {
              ...cur,
              connected: presence.connected,
              lastSeen: presence.lastSeen,
            }
          : cur
      );
    };
    callbackRef.current = handler;
    subscribeToPresence(u.id, handler, client);
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (user && callbackRef.current) {
        unsubscribeFromPresence(user.id, callbackRef.current);
      }
    };
  }, []);

  return [user, setUser] as const;
}
